import { useEffect, useId, useMemo, useState } from "react";
import { BaseModal, ModalCloseCross, type BaseModalProps } from "@/components/modals/BaseModal";
import { useQueryClient } from "@tanstack/react-query";
import { Constantes } from "@/ts/constantes.js";
import { notificacion, notificacionError, notificacionTipo } from "@/components/Alerts.js";
import { TablaComponentes } from "./TablaComponentes.js";
import { QueryError } from "@/components/Loading/Loading.js";
import { lanzarOF, lanzarOPE, useDatosFabricacion, type iInputsCrearOF, type InfoComponente } from "@/hooks/Api/OrdenesFabricacion.js";
import { FormArticulo } from "./FormArticulo.js";
import { FormComponente } from "./FormComponente.js";
import { toast } from "react-toastify";

interface ModalCrearOFProps extends BaseModalProps {
	readonly idArticulo: string | null;
}
export function ModalCrearOF(props: ModalCrearOFProps) {
	const queryClient = useQueryClient();

	const { data, error } = useDatosFabricacion(props.idArticulo);
	const tipoArticulo = useMemo(() => {
		if (data == null) return "";

		switch (data.tipoArticulo) {
			case "OF":
				if (data.ruta.fases.some(fase => fase.xcentro_id == "EXT")) return ("OF+OPE");
				else return ("OF");
			case "OPE":
				return ("OPE");
			case "Compras":
				if (data.ruta.fases.length > 0) toast("El articulo es de tipo compras pero tiene una ruta de fabricacion!", {type: "warning"});
				return "Compras";
			default:
				return data.tipoArticulo;
		}
	}, [data]);

	const handleSubmit = async (idArticulo: string, value: iInputsCrearOF) => {
		if (data == null) throw new Error("No hay datos de fabricación disponibles");

		if (tipoArticulo == "OPE") {
			const response = await lanzarOPE(idArticulo, value);
			if (response.tipo == "error") {
				notificacionError(response.message ?? "Error añadiendo OPE");
				throw new Error(response.message ?? "Error añadiendo OPE");
			}

			notificacion(response.message ?? "OPE añadida correctamente");
		} else if (tipoArticulo == "OF" || tipoArticulo == "OF+OPE") {
			const response = await lanzarOF({
				idConjunto: idArticulo,
				values: value,
				componentes: componentes,
				sendFases: data.ruta.fases.length > 1,
				textoOF: value.textoOF
			});

			if (response.tipo == "error") {
				notificacionTipo("error", response.message ?? "Error lanzando OF");
				throw new Error(response.message ?? "Error lanzando OF");
			}

			if (response.tipo == "warning") {
				notificacionTipo("warning", response.message ?? "OF lanzada con advertencias");
			} else {
				notificacionTipo("success", response.message ?? "OF lanzada correctamente");
			}
		} else {
			notificacionError("Tipo de artículo no válido");
			console.error("Tipo de artículo no válido", tipoArticulo);
			throw new Error("Tipo de artículo no válido");
		}


		await queryClient.invalidateQueries({ queryKey: ["getOrdenesConjunto", { idConjunto: idArticulo }] });
		await queryClient.invalidateQueries({ queryKey: ["getArticulosStock"] });
	};

	const [componentes, setComponentes] = useState<InfoComponente[]>(data?.componentes ?? []);
	useEffect(() => {
		//TODO esto hay que cambiarlo por dirtyComponents y pristineComponents, y tal
		// eslint-disable-next-line react-hooks/set-state-in-effect
		setComponentes(data?.componentes ?? []);
	}, [data?.componentes]);

	const formId = useId();

	const showOPE = tipoArticulo === "OPE" || tipoArticulo === "OF+OPE";
	const showOF = tipoArticulo === "OF" || tipoArticulo === "OF+OPE";

	if (error) return <QueryError error={error} fallback="Error al cargar los datos de fabricación" />;


	return (
		<BaseModal show={props.show} close={props.close}>
			<div className="modal-header">
				<h4 className="modal-title">{showOPE ? "Orden de Producción Externa (OPE)" : "Orden de Fabricación (OF)"}</h4>
				<ModalCloseCross onClick={props.close} />
			</div>
			<div className="modal-body" style={{ minWidth: "60vw" }}>
				<div style={{ marginBottom: 20 }}>
					<h1 style={{ color: "#111", fontSize: 32, margin: 0 }} >
						{data?.descripcion ?? "..."}
					</h1>
					<h2 style={{ margin: 0 }}>
						<span>Conjunto:</span>
						<span style={{ color: "#666666", fontWeight: "bold" }} >
							{props.idArticulo}
						</span>
					</h2>
				</div>

				<FormArticulo
					id={formId}
					key={props.idArticulo}
					onSubmit={handleSubmit}
					closeModal={props.close}
					data={data ?? null}
					idArticulo={props.idArticulo}
					tipoArticulo={tipoArticulo}
					showOF={showOF}
					showOPE={showOPE}
				/>


				<div style={{ marginTop: 35 }}>


					<div className="table-responsive" style={{ marginTop: 20, borderBottom: "1px solid #111", display: showOF ? "contents" : "none" }}>
						<label className="labelsForm" style={{ width: "100%" }} >Componentes</label>
						<TablaComponentes
							data={componentes}
							setData={setComponentes}
							fasesRuta={data?.ruta.fases ?? []}
						/>
						{Constantes.elegirComponentesOF == true && (
							<FormComponente data={componentes} setData={setComponentes} />
						)}

					</div>
				</div>
			</div>
			<div className="modal-footer">
				<button type="button" className="btnSecondary" onClick={props.close}>Cerrar</button>
				<button type="submit" className="btnPrimary" form={formId}>Lanzar Orden</button>
			</div>
		</BaseModal >
	);
}
